<template>
	<article class="chart_card" :class="{ 'is-comparison': comparisonOpen }">
		<!-- Sin título propio: "Evolución de Ventas & Márgenes" ya es el título
			colapsable del header sticky del tab. La barra fina sólo lleva el
			toggle Comparación ↔ Ver Barra (mismo léxico que el de la tabla de
			evolución de Ventas). -->
		<div class="chart_card_bar">
			<button type="button" class="action_btn"
				:class="{ 'is-active': comparisonOpen }"
				:aria-pressed="String(comparisonOpen)"
				@click="$emit('toggle-comparison', !comparisonOpen)">
				<component :is="comparisonOpen ? 'DashboardSquare' : 'ChartLine'"
					class="action_icon" />
				{{ comparisonOpen ? "Ver Barra" : "Comparación" }}
			</button>
		</div>

		<!-- Modo comparación: una línea por año para la métrica elegida en los
			chips (SalesComparisonChart, el mismo de Ventas/Costos/Gastos). -->
		<SalesComparisonChart v-if="comparisonOpen"
			v-bind="comparison"
			@select-item="$emit('comparison-select-metric', $event)"
			@add-year="$emit('comparison-add-year', $event)"
			@remove-year="$emit('comparison-remove-year', $event)" />

		<div v-else class="chart_canvas">
			<p v-if="error" class="chart_state chart_state--error">{{ error }}</p>
			<p v-else-if="isLoading" class="chart_state">Cargando…</p>
			<p v-else-if="!hasData" class="chart_state">
				Sin datos para el período seleccionado.
			</p>
			<KpiComposedChart v-else
				:monthHeaders="headers"
				:series="series"
				showSymbols />
		</div>
	</article>
</template>

<script>
import ChartLine from "@/components/svg/ChartLine.vue";
import DashboardSquare from "@/components/svg/DashboardSquare.vue";
import KpiComposedChart from "@/components/financialReports/charts/KpiComposedChart.vue";
import SalesComparisonChart from "@/components/financialReports/charts/SalesComparisonChart.vue";

// Card del gráfico "Evolución de Ventas & Márgenes" del tab Rentabilidad
// (docs/decisiones/26-rentabilidad-grafico-ventas-y-margenes.md): barras de Ventas ($, eje
// izquierdo) y una línea por escalón de margen (%, eje derecho) sobre las
// MISMAS filas y cabeceras que la tabla de abajo (`financialReports`
// tableRowHeaders / getHeadersTable) — no hay fetch propio, así el gráfico y
// la tabla nunca se desincronizan. Recibe todo por props y no importa Vuex,
// igual que SalesChartsRow.
//
// Cada margen fija su color por token del tema (identidad por escalón, la
// misma de los progress bars de las cards históricas): en Servicios faltan
// Producto y Contribución y los demás conservan su color.
//
// Modo comparación: el botón alterna a SalesComparisonChart (líneas por año)
// con el view-model que arma el tab (`comparison`, sus keys calzan 1:1 con las
// props del chart); la card sólo re-emite las interacciones al tab, que las
// cablea al store — mismo reparto que SalesEvolutionTable con VentasTab.
const SALES_KEY = "ventas_netas";
const SALES_NAME = "Ventas";
const MARGIN_LINES = [
	{ key: "margen_producto", name: "Margen Producto", colorToken: "--report-chart-line-1", productsOnly: true },
	{ key: "margen_bruto", name: "Margen Bruto", colorToken: "--report-chart-line-2" },
	{ key: "margen_contribucion", name: "Margen Contribución", colorToken: "--report-chart-line-3", productsOnly: true },
	{ key: "margen_EBITDA_%", name: "Margen EBITDA", colorToken: "--report-chart-line-4" },
	{ key: "margen_neto_%", name: "Margen Neto", colorToken: "--report-chart-line-5" },
];

function isNumber(value) {
	return typeof value === "number" && Number.isFinite(value);
}

export default {
	name: "ProfitabilityChartCard",

	components: { ChartLine, DashboardSquare, KpiComposedChart, SalesComparisonChart },

	props: {
		// Cabeceras del rango activo (getHeadersTable), en orden cronológico.
		headers: { type: Array, default: () => [] },
		// `tableRowHeaders` del store: { [key]: { itemsDict: { [header]: n } } }.
		rows: { type: Object, default: () => ({}) },
		// Productos muestra los cinco escalones; Servicios solo Bruto, EBITDA
		// y Neto (mismo criterio que las filas de la tabla).
		isProductsBusiness: { type: Boolean, default: false },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: "" },
		// Modo comparación (estado del tab): abierto/cerrado y el view-model
		// para SalesComparisonChart ({series, months, years, availableYears,
		// subItems, selectedItem, period, valueFormat, isLoading, error}).
		comparisonOpen: { type: Boolean, default: false },
		comparison: { type: Object, default: () => ({}) },
	},

	computed: {
		// Ventas por cabecera; `null` donde el store no trae la cabecera.
		salesValues() {
			return this.headers.map((header) => this.valueAt(SALES_KEY, header));
		},

		hasData() {
			return this.headers.length > 0 && this.salesValues.some((value) => isNumber(value));
		},

		// [Ventas (bar, money), …márgenes (line, percent)]. El margen de una
		// cabecera sin ventas viaja `null` (la línea se corta): el store
		// devuelve 0 % al dividir por cero y un 0 % dibujado mentiría.
		series() {
			const sales = {
				name: SALES_NAME,
				type: "bar",
				unit: "money",
				data: this.salesValues,
			};
			const lines = MARGIN_LINES
				.filter((spec) => this.isProductsBusiness || !spec.productsOnly)
				.map((spec) => ({
					name: spec.name,
					type: "line",
					unit: "percent",
					colorToken: spec.colorToken,
					data: this.headers.map((header, i) => (
						this.salesValues[i] ? this.valueAt(spec.key, header) : null
					)),
				}));
			return [sales, ...lines];
		},
	},

	methods: {
		valueAt(key, header) {
			const row = this.rows[key];
			const value = row && row.itemsDict ? row.itemsDict[header] : undefined;
			return isNumber(value) ? value : null;
		},
	},
};
</script>

<style scoped lang="scss">
// Misma chrome que las cards de SalesChartsRow (surface + borde suave +
// radio 16 + whisper shadow), a ancho completo. El canvas del gráfico
// compuesto mide lo de las otras cards (340 px menos la barra); en modo
// comparación la card crece a lo que necesite SalesComparisonChart (leyenda
// de años + chips + su chart de 340 px).
.chart_card {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 16px;
	box-shadow: var(--report-shadow-card);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	padding: 10px 16px 16px;
	position: relative;
}

// Barra fina, acciones a la derecha (como EvolutionTableTitleBar sin título).
.chart_card_bar {
	display: flex;
	justify-content: flex-end;
	align-items: center;
}

// En modo barras el botón se monta SOBRE la esquina del gráfico, a la altura
// de la leyenda que ECharts dibuja arriba a la izquierda: así comparten
// renglón y no queda una banda vacía entre el botón y la leyenda. El `top`
// centra el botón (~30 px) con la fila de la leyenda (~24 px desde el borde
// de la card).
.chart_card:not(.is-comparison) .chart_card_bar {
	position: absolute;
	top: 6px;
	right: 16px;
	z-index: 2;
}

// En comparación la leyenda es propia del chart (años + "Añadir año") y ocupa
// esa esquina: ahí el botón se queda en el flujo, con su renglón.
.chart_card.is-comparison .chart_card_bar {
	margin-bottom: 8px;
}

.action_btn {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover { background: var(--report-surface); }

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}

	// Activo mientras la comparación está abierta.
	&.is-active {
		background: var(--report-accent-bg);
		border-color: var(--report-accent-border);
		color: var(--report-accent-text);
	}
}

.action_icon {
	width: 16px;
	height: 16px;
	margin-right: 4px;
}

.chart_canvas {
	height: 300px;
	min-height: 0;
	display: flex;
	align-items: stretch;
	justify-content: stretch;
	position: relative;
}

.chart_state {
	margin: auto;
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;

	&--error {
		color: var(--report-negative);
	}
}
</style>
